import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from 'react-router-dom';
import Axios, { markPageUnloading } from '@/shared/api/axios';
import { useUnmountSafeTimeout } from '@/hooks/useUnmountSafeTimeout';

import PageLayout from '@/components/layout/PageLayout';
import LeftSidebar from '@/components/layout/LeftSidebar';
import RightSidebar from '@/components/layout/RightSidebar';
import PostCard from '@/features/posts/components/PostCard';
import StatusModal from '@/components/ui/StatusModal';

function PostDetail() {
  const userId = useSelector((state) => state.user.idolId);

  const [isLoading, setIsLoading] = useState(false);
  const [postInfo, setPostInfo] = useState(null);

  const { postUuid } = useParams();
  const navigate = useNavigate();
  const setRemovedRefetchTimeout = useUnmountSafeTimeout();

  const [params] = useState({
    idol_id: userId,
    post_uuid: postUuid
  });

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  const getPost = async() => {
    setIsLoading(true);
    try {
      const res = await Axios.post("/post/get-only", params);
      switch (res.data.status_code) {
        case 'SYSTEM_1000':
          setPostInfo(res.data.data);
          break;
        case 'SYSTEM_1001':
          // 貼文不存在或參數錯誤時，導向 404
          navigate('/404', { replace: true });
          break;
        default:
          setCurrentStatusCode(res.data.status_code);
          statusModalRef.current?.show();
          break;
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('get-only error', error);
      }
    } finally {
      setIsLoading(false);
    }
  }

  // 點擊 tags 導向首頁顯示相關貼文和世界
  const updateListWithTag = (tag) => {
      markPageUnloading();
      window.location.href = `/?tags=${tag}`;
  };

  // 本地更新單篇貼文，取代整包 refetch（避免按讚/收藏/留言時畫面閃動）
  // updater 回傳 null/undefined 視為「移除」語意（例如刪除貼文），單篇頁面沒有列表可移除，
  // 改用既有 getPost() 重新請求，讓後端回 SYSTEM_1001 時走原本的 404 導頁流程
  // PostCard 傳入的 updater 都是純函式，且「是否回傳 null」與傳入的資料無關（所以 await 後舊閉包的 postInfo 也能拿來試算），
  // 先試算一次判斷是否為「移除」。本頁不可傳 removeOnUncollect 給 PostCard：取消收藏成功後會送出移除（回傳 null 的 updater），會被當成刪除處理；
  // 不在 setState 的 updater 裡設旗標再同步讀取：那依賴 React 的 eager state 優化，不保證 updater 當下就執行
  const patchPost = (updater) => {
    if (!postInfo) return;
    if (!updater(postInfo)) {
      // 刪除：先讓刪除成功提示停留（與世界觀詳情頁刪除後 3 秒才導頁一致），再重抓走 404 導頁；
      // 立刻重抓的話提示只顯示約 0.25 秒就被 404 頁換掉。期間離開頁面時不重抓
      setRemovedRefetchTimeout(getPost, 3000);
      return;
    }
    setPostInfo(prev => (prev ? updater(prev) : prev));
  };

  useEffect(() => {
    getPost();
  },[])

  return (
    <PageLayout
      leftSidebar={<LeftSidebar />}
      rightSidebar={<RightSidebar />}
      isLoading={isLoading}
    >
      <div className="activity-items">
        {postInfo ?
          <PostCard data={postInfo} contentType="post" showFullContent={true} onItemChange={patchPost} updateList={getPost} onTagClick={updateListWithTag} />
          : ''}
      </div>
      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
    </PageLayout>
  )
}

// 以網址上的 uuid 當 key：站內從一篇切到另一篇（例如點通知）時重新掛載。
// 頁面只在掛載時抓資料，不重新掛載的話畫面會停在前一篇，前一篇的延遲計時器（刪除後導頁）也不會清掉
function PostDetailRoute() {
  const { postUuid } = useParams();
  return <PostDetail key={postUuid} />;
}

export default PostDetailRoute;
